import type { AssetMedia } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { cva } from 'class-variance-authority'
import { useCallback, useState } from 'react'
import { ActivityIndicator, Platform, View } from 'react-native'
import Animated, { FadeInUp } from 'react-native-reanimated'
import CloseButton from '~/components/CloseButton.tsx'
import { Detail } from '~/components/typography/Content.tsx'
import designTokens from '~/lib/designTokens'
import { useFileUpload } from '~/lib/file-upload/hooks'
import { useUploadStrategies } from '~/lib/file-upload/strategies'
import {
  defaultImageUploadAccept,
  formatAcceptedFileType,
  formatFileSize,
} from '~/lib/file-upload/utils'
import { cn } from '~/lib/utils'
import Icon from './Icon'
import { Button, ButtonLabel, buttonTextVariants, buttonVariants } from './ui/button'
import { Input } from './ui/input'
import { Text } from './ui/text'

interface FileUploadProps {
  bucket?: string
  onUpload?: (media: AssetMedia) => void
  onError?: (error: string) => void
  accept?: string[]
  maxSizeBytes?: number
  className?: string
  multiple?: boolean
  disabled?: boolean
  order?: number
  variant?: 'default' | 'transparent'
}

export function FileUpload({
  bucket = 'property-media',
  onUpload,
  onError,
  accept = defaultImageUploadAccept,
  maxSizeBytes = 50 * 1024 * 1024,
  className,
  disabled = false,
  order = 0,
  variant = 'default',
}: FileUploadProps) {
  const { t } = useTranslations('common')
  const [urlInput, setUrlInput] = useState('')
  const [showUrlInput, setShowUrlInput] = useState(false)

  const { processFile, processUrl, isUploading } = useFileUpload({
    bucket,
    maxSizeBytes,
    accept,
    order,
    onUpload,
    onError,
  })

  const { handleFileChange, pickImage, takePhoto } = useUploadStrategies({
    onFileSelect: processFile,
    onError: (error) => onError?.(error),
  })

  const handleUrlSubmit = useCallback(() => {
    if (!urlInput.trim()) {
      return
    }

    void processUrl(urlInput.trim())
    setUrlInput('')
    setShowUrlInput(false)
  }, [urlInput, processUrl])

  const panelVariants = cva('rounded-lg border p-4', {
    variants: {
      variant: {
        default: 'border-border bg-card shadow-sm',
        transparent: 'web:backdrop-blur-sm border-neutral-400/30 bg-background/30',
      },
    },
    defaultVariants: { variant: 'default' },
  })

  if (isUploading) {
    return (
      <View className={cn(panelVariants({ variant }), className)}>
        <View className="flex-row items-center justify-center gap-3">
          <ActivityIndicator size="small" color={designTokens.foreground} />
        </View>
      </View>
    )
  }

  return (
    <Animated.View className={cn('gap-4', className)} entering={FadeInUp}>
      <View className="flex-row flex-wrap items-center gap-2">
        {Platform.OS === 'web' ? (
          <label
            className={cn(
              buttonVariants({
                variant: 'outline',
                size: 'sm',
              }),
              'web:pointer-events-auto inline-flex flex-initial shrink cursor-pointer items-center gap-2 py-2.5 pl-4'
            )}>
            <input
              type="file"
              accept={accept.join(',')}
              onChange={handleFileChange}
              className="hidden"
            />
            <Icon
              name="cloud-upload"
              size={16}
              className="shrink-0 group-hover/button:text-accent group-active/button:text-accent"
            />
            <Text
              className={buttonTextVariants({
                variant: 'outline',
                size: 'sm',
              })}>
              {t(($) => $.fileUpload.selectFile)}
            </Text>
          </label>
        ) : (
          <>
            <Button
              variant="outline"
              onPress={pickImage}
              disabled={disabled}
              size="sm"
              className="flex-row items-center gap-2 py-2.5 pl-4">
              <Icon name="cloud-upload" size={16} className="shrink-0" />
              <ButtonLabel>{t(($) => $.fileUpload.selectFile)}</ButtonLabel>
            </Button>
            {accept.some((type) => type.startsWith('image/')) && (
              <Button
                variant="outline"
                size="sm"
                onPress={takePhoto}
                disabled={disabled}
                className="flex-row items-center gap-2 py-2.5 pl-4">
                <Icon name="camera" size={16} className="shrink-0" />
                <ButtonLabel>{t(($) => $.fileUpload.takePhoto)}</ButtonLabel>
              </Button>
            )}
          </>
        )}

        <Button
          variant="outline"
          onPress={() => setShowUrlInput(!showUrlInput)}
          disabled={disabled}
          size="sm"
          className="py-2.5">
          <Icon name="link" size={16} className="shrink-0 text-foreground" />
        </Button>
      </View>

      {showUrlInput && (
        <View className={cn(panelVariants({ variant }), 'w-full gap-2 p-3')}>
          <Text className="font-light text-xs">{t(($) => $.fileUpload.uploadFromUrl)}</Text>
          <Input
            value={urlInput}
            onChangeText={setUrlInput}
            // eslint-disable-next-line i18next/no-literal-string
            placeholder="https://example.com/image.jpg"
            className="w-full flex-1"
            autoCapitalize="none"
            autoCorrect={false}
          />
          <View className="flex-1 flex-row justify-between gap-2">
            <CloseButton
              onPress={() => {
                setShowUrlInput(false)
                setUrlInput('')
                return false
              }}
              className="static"
            />
            <Button
              onPress={handleUrlSubmit}
              disabled={!urlInput.trim() || disabled}
              size="sm"
              className="flex-row items-center gap-2">
              <Icon name="cloud-upload" size={16} className="shrink-0" />
              <ButtonLabel>{t(($) => $.fileUpload.upload)}</ButtonLabel>
            </Button>
          </View>
        </View>
      )}

      <View className="gap-0">
        <Detail className="text-pretty text-center">
          {t(($) => $.fileUpload.supportedTypes)}{' '}
          <Text className="font-semibold">{accept.map(formatAcceptedFileType).join(', ')}</Text>
        </Detail>
        <Detail className="text-pretty text-center">
          {t(($) => $.fileUpload.maxSize)}{' '}
          <Text className="font-semibold">{formatFileSize(maxSizeBytes)}</Text>
        </Detail>
      </View>
    </Animated.View>
  )
}
